// Tính toán cho Dashboard kinh doanh: khoảng thời gian + kỳ so sánh, phễu xuyên phòng ban,
// chỉ số tổng hợp và các cảnh báo tự động từ dữ liệu Trực page / Telesale / Sale cơ sở.
import dayjs, { type Dayjs } from 'dayjs';
import type { MarketingReportV2Data, PagePerformanceData, SaleBranchPerformanceData, TelesalePerformanceData } from 'api/DashboardStatistics';
import type { AppointmentStatusSummaryItem } from 'api/Appointment';

export type RangeKey = 'today' | 'yesterday' | '7d' | '30d' | 'month' | 'lastMonth' | 'custom';

export const RANGE_LABEL: Record<Exclude<RangeKey, 'custom'>, string> = {
  today: 'Hôm nay', yesterday: 'Hôm qua', '7d': '7 ngày', '30d': '30 ngày', month: 'Tháng này', lastMonth: 'Tháng trước',
};

export interface Range {
  key: RangeKey;
  start: Dayjs;
  end: Dayjs;
  prevStart: Dayjs;
  prevEnd: Dayjs;
  /** Mô tả kỳ so sánh, ví dụ "so với hôm qua" */
  prevLabel: string;
  /** Kỳ đang xem có chứa hôm nay không — dùng để bật tự làm mới */
  live: boolean;
}

const D = 'YYYY-MM-DD';
export const fmtD = (d: Dayjs) => d.format(D);

export function resolveRange(key: RangeKey, from?: string | null, to?: string | null, now: Dayjs = dayjs()): Range {
  const today = now.startOf('day');
  let start = today;
  let end = today;
  let prevStart: Dayjs;
  let prevEnd: Dayjs;
  let prevLabel: string;
  const shift = () => {
    const days = end.diff(start, 'day') + 1;
    prevEnd = start.subtract(1, 'day');
    prevStart = prevEnd.subtract(days - 1, 'day');
    prevLabel = `so với ${days} ngày trước đó`;
  };
  switch (key) {
    case 'yesterday':
      start = end = today.subtract(1, 'day');
      prevStart = prevEnd = today.subtract(2, 'day');
      prevLabel = 'so với hôm kia';
      break;
    case '7d':
      start = today.subtract(6, 'day');
      shift();
      prevLabel = 'so với 7 ngày trước đó';
      break;
    case '30d':
      start = today.subtract(29, 'day');
      shift();
      prevLabel = 'so với 30 ngày trước đó';
      break;
    case 'month': {
      start = today.startOf('month');
      const pm = start.subtract(1, 'month');
      prevStart = pm;
      // cùng kỳ tháng trước: 1 → cùng ngày (hoặc cuối tháng nếu tháng trước ngắn hơn)
      prevEnd = pm.date(Math.min(today.date(), pm.daysInMonth()));
      prevLabel = 'so với cùng kỳ tháng trước';
      break;
    }
    case 'lastMonth':
      start = today.subtract(1, 'month').startOf('month');
      end = start.endOf('month').startOf('day');
      prevStart = start.subtract(1, 'month');
      prevEnd = prevStart.endOf('month').startOf('day');
      prevLabel = 'so với tháng liền trước';
      break;
    case 'custom': {
      const f = from ? dayjs(from, D) : null;
      const t = to ? dayjs(to, D) : null;
      if (f?.isValid() && t?.isValid() && !t.isBefore(f)) { start = f.startOf('day'); end = t.startOf('day'); }
      shift();
      break;
    }
    default:
      prevStart = prevEnd = today.subtract(1, 'day');
      prevLabel = 'so với hôm qua';
  }
  return { key, start, end, prevStart: prevStart!, prevEnd: prevEnd!, prevLabel: prevLabel!, live: !end.isBefore(today) };
}

export interface Snapshot {
  page: PagePerformanceData | null;
  tele: TelesalePerformanceData | null;
  sale: SaleBranchPerformanceData | null;
  /** Báo cáo chiến dịch quảng cáo (chi phí, tin nhắn, khách, doanh thu theo chiến dịch) */
  mkt?: MarketingReportV2Data | null;
  /** Số lịch hẹn theo trạng thái (Đặt lịch mới, Đã xác nhận, Chờ thực hiện, Đã thực hiện, Hủy lịch) */
  appt?: AppointmentStatusSummaryItem[] | null;
}

export const EMPTY_SNAPSHOT: Snapshot = { page: null, tele: null, sale: null, mkt: null };

export type Dept = 'page' | 'tele' | 'sale';
export const DEPT: Record<Dept, { label: string; color: string; soft: string }> = {
  page: { label: 'Trực page', color: '#378ADD', soft: '#EAF3FC' },
  tele: { label: 'Telesale', color: '#7F77DD', soft: '#F0EFFB' },
  sale: { label: 'Sale cơ sở', color: '#1D9E75', soft: '#E6F5EF' },
};
export const MKT = { label: 'Marketing', color: '#D85A30', soft: '#FBEEE8' };
/** Thông tin hiển thị cho nguồn của một cảnh báo */
export const sourceMeta = (d: Dept | 'mkt' | 'all') => (d === 'all' ? null : d === 'mkt' ? MKT : DEPT[d]);

const n = (v: unknown) => (Number.isFinite(Number(v)) ? Number(v) : 0);
/** Tỷ lệ phần trăm a/b, trả null khi không có mẫu số */
export const ratio = (a: number, b: number) => (b > 0 ? (a / b) * 100 : null);

/** Các con số chính, gom từ 3 API */
export function totals(s: Snapshot) {
  const chats = n(s.page?.summary.total_chat_customers);
  const leads = n(s.page?.summary.total_new_customers);
  const pageAppts = n(s.page?.summary.total_appointments);
  const booked = n(s.tele?.summary.total_booked);
  const teleShowup = n(s.tele?.summary.total_showup);
  const teleClosed = n(s.tele?.summary.total_closed);
  const showup = n(s.sale?.summary.total_showup);
  const closed = n(s.sale?.summary.total_closed);
  const revenue = n(s.sale?.summary.total_revenue);
  return {
    chats, leads, pageAppts, booked, teleShowup, teleClosed, showup, closed, revenue,
    leadRate: ratio(leads, chats),
    // mẫu số = 0 thì không có tỷ lệ (BE trả 0) → so sánh hiện "—" thay vì "+52,6 điểm"
    showupRate: booked > 0 ? n(s.tele?.summary.showup_rate) : null,
    teleCloseRate: teleShowup > 0 ? n(s.tele?.summary.close_rate) : null,
    closeRate: showup > 0 ? n(s.sale?.summary.close_rate) : null,
    aov: n(s.sale?.summary.avg_order_value),
    teleRevenue: n(s.tele?.revenue?.current_period),
    spend: n(s.mkt?.summary.total_spent),
    budget: n(s.mkt?.summary.total_budget),
    adMessages: n(s.mkt?.summary.ad_messages),
    organicMessages: n(s.mkt?.summary.organic_messages),
    adRevenue: n(s.mkt?.summary.total_revenue),
    adLeads: campaignRows(s.mkt).reduce((t, r) => t + r.leads, 0),
  };
}
export type Totals = ReturnType<typeof totals>;

export interface PipelineStep { key: string; label: string; dept: Dept; value: number; conv: number | null; prev: number; info: string }

/** Phễu xuyên phòng ban: tin nhắn → khách mới → lịch hẹn → đến cơ sở → chốt */
export function pipeline(cur: Totals, prev: Totals): PipelineStep[] {
  const raw: Omit<PipelineStep, 'conv'>[] = [
    { key: 'chats', label: 'Khách nhắn tin', dept: 'page', value: cur.chats, prev: prev.chats, info: 'Khách chat mới trên các fanpage (Trực page)' },
    { key: 'leads', label: 'Khách hàng mới', dept: 'page', value: cur.leads, prev: prev.leads, info: 'Khách được Trực page tạo hồ sơ (đã lấy được thông tin liên hệ)' },
    { key: 'booked', label: 'Lịch hẹn đã đặt', dept: 'tele', value: cur.booked, prev: prev.booked, info: 'Lịch hẹn do Telesale chốt được trong kỳ' },
    { key: 'showup', label: 'Khách đến cơ sở', dept: 'sale', value: cur.showup, prev: prev.showup, info: 'Khách có mặt tại cơ sở (Sale cơ sở)' },
    { key: 'closed', label: 'Khách chốt dịch vụ', dept: 'sale', value: cur.closed, prev: prev.closed, info: 'Khách mua dịch vụ tại cơ sở' },
  ];
  return raw.map((s, i) => ({ ...s, conv: i === 0 ? null : ratio(s.value, raw[i - 1].value) }));
}

/** Bước chuyển đổi giảm mạnh nhất so với kỳ trước (điểm nghẽn) */
export function bottleneck(cur: PipelineStep[], prev: PipelineStep[]) {
  let worst: { step: PipelineStep; from: string; drop: number } | null = null;
  cur.forEach((s, i) => {
    const p = prev[i];
    if (i === 0 || s.conv === null || p?.conv == null || cur[i - 1].value < 10) return;
    const drop = p.conv - s.conv;
    if (drop >= 5 && (!worst || drop > worst.drop)) worst = { step: s, from: cur[i - 1].label, drop };
  });
  return worst as { step: PipelineStep; from: string; drop: number } | null;
}

export type AlertLevel = 'critical' | 'warning' | 'good';
export interface Insight { id: string; level: AlertLevel; dept: Dept | 'mkt' | 'all'; title: string; detail: string }

/** Ngưỡng cảnh báo */
export const RULES = {
  /** Giảm so với kỳ trước từ mức này trở lên thì cảnh báo */
  dropPct: 20,
  /** Kỳ trước phải đủ lớn để so sánh có ý nghĩa */
  minBase: 10,
  /** Nhân sự kém hơn trung bình đội theo tỷ lệ này (0.7 = thấp hơn 30%) */
  lagFactor: 0.7,
  /** Số lượng tối thiểu để đánh giá một nhân sự */
  minVolume: 5,
  /** Tỷ lệ khách chưa được gọi trên tổng kết quả cuộc gọi */
  uncalledShare: 30,
};

const fmtN = (v: number) => Math.round(v).toLocaleString('vi-VN');
const fmtP = (v: number) => `${v.toFixed(1).replace('.', ',')}%`;

export function insights(cur: Snapshot, prev: Snapshot, prevLabel: string): Insight[] {
  const out: Insight[] = [];
  const c = totals(cur);
  const p = totals(prev);

  // 1. Chỉ số giảm mạnh so với kỳ trước
  const drops: { id: string; dept: Dept; label: string; c: number; p: number }[] = [
    { id: 'chats', dept: 'page', label: 'Khách nhắn tin', c: c.chats, p: p.chats },
    { id: 'leads', dept: 'page', label: 'Khách hàng mới', c: c.leads, p: p.leads },
    { id: 'booked', dept: 'tele', label: 'Lịch hẹn đã đặt', c: c.booked, p: p.booked },
    { id: 'showup', dept: 'sale', label: 'Khách đến cơ sở', c: c.showup, p: p.showup },
    { id: 'closed', dept: 'sale', label: 'Khách chốt', c: c.closed, p: p.closed },
    { id: 'revenue', dept: 'sale', label: 'Doanh thu', c: c.revenue, p: p.revenue },
  ];
  drops.forEach(d => {
    if (d.p < RULES.minBase && d.id !== 'revenue') return;
    if (d.p <= 0) return;
    const pct = ((d.c - d.p) / d.p) * 100;
    if (pct <= -RULES.dropPct) {
      out.push({
        id: `drop-${d.id}`, level: pct <= -40 ? 'critical' : 'warning', dept: d.dept,
        title: `${d.label} giảm ${Math.abs(Math.round(pct))}%`,
        detail: d.id === 'revenue' ? `${prevLabel}.` : `${fmtN(d.c)} so với ${fmtN(d.p)} ${prevLabel.replace(/^so với /, '')}.`,
      });
    }
  });

  // 2. Điểm nghẽn trong phễu
  const b = bottleneck(pipeline(c, p), pipeline(p, p));
  if (b) {
    out.push({
      id: 'bottleneck', level: b.drop >= 15 ? 'critical' : 'warning', dept: b.step.dept,
      title: `Điểm nghẽn: ${b.from} → ${b.step.label}`,
      detail: `Tỷ lệ chuyển đổi còn ${fmtP(b.step.conv!)}, giảm ${b.drop.toFixed(1).replace('.', ',')} điểm ${prevLabel}.`,
    });
  }

  // 3. Khách chưa được gọi (kết quả cuộc gọi id = 0)
  const calls = cur.page?.call_result_summary || [];
  const callTotal = calls.reduce((s, x) => s + n(x.count), 0);
  // API trả "Chưa gọi" với id = null nên nhận diện theo tên trước
  const uncalled = calls.find(x => /chưa gọi/i.test(x.name)) ?? calls.find(x => x.id === 0);
  if (uncalled && callTotal >= RULES.minVolume) {
    const share = (n(uncalled.count) / callTotal) * 100;
    if (share >= RULES.uncalledShare) {
      out.push({
        id: 'uncalled', level: share >= 50 ? 'critical' : 'warning', dept: 'tele',
        title: `${fmtN(n(uncalled.count))} khách chưa được gọi`,
        detail: `Chiếm ${fmtP(share)} khách cần liên hệ trong kỳ — cần phân bổ thêm cho Telesale.`,
      });
    }
  }

  // 4. Nhân sự tụt hậu so với trung bình đội
  const teleRows = cur.tele?.rankings || [];
  const teleAvgShow = c.showupRate ?? 0;
  teleRows
    .filter(r => n(r.booked) >= RULES.minVolume && teleAvgShow > 0 && n(r.showup_rate) < teleAvgShow * RULES.lagFactor)
    .sort((a, b) => n(a.showup_rate) - n(b.showup_rate))
    .slice(0, 2)
    .forEach(r => out.push({
      id: `tele-${r.staff_id}`, level: 'warning', dept: 'tele',
      title: `${r.name}: tỷ lệ khách đến ${fmtP(n(r.showup_rate))}`,
      detail: `Thấp hơn trung bình đội (${fmtP(teleAvgShow)}) — ${fmtN(n(r.booked))} lịch đặt, ${fmtN(n(r.showup))} khách đến.`,
    }));

  const saleRows = cur.sale?.rankings || [];
  const saleAvg = c.closeRate ?? 0;
  saleRows
    .filter(r => n(r.showup) >= RULES.minVolume && saleAvg > 0 && n(r.rate) < saleAvg * RULES.lagFactor)
    .sort((a, b) => n(a.rate) - n(b.rate))
    .slice(0, 2)
    .forEach(r => out.push({
      id: `sale-${r.staff_id}`, level: n(r.closed) === 0 ? 'critical' : 'warning', dept: 'sale',
      title: `${r.name}: tỷ lệ chốt ${fmtP(n(r.rate))}`,
      detail: `Thấp hơn trung bình đội (${fmtP(saleAvg)}) — tiếp ${fmtN(n(r.showup))} khách, chốt ${fmtN(n(r.closed))}.`,
    }));

  const pageRows = cur.page?.staff_customer_stats || [];
  const pageAvg = c.leadRate ?? 0;
  pageRows
    .filter(r => n(r.channel_customer_count) >= RULES.minVolume * 4 && pageAvg > 0)
    .map(r => ({ r, rate: (n(r.customer_count) / n(r.channel_customer_count)) * 100 }))
    .filter(x => x.rate < pageAvg * RULES.lagFactor)
    .sort((a, b) => a.rate - b.rate)
    .slice(0, 2)
    .forEach(({ r, rate }) => out.push({
      id: `page-${r.staff_id}`, level: 'warning', dept: 'page',
      title: `${r.staff_name}: tỷ lệ lấy số ${fmtP(rate)}`,
      detail: `Thấp hơn trung bình đội (${fmtP(pageAvg)}) — ${fmtN(n(r.channel_customer_count))} khách chat, ${fmtN(n(r.customer_count))} khách mới.`,
    }));

  // 5. Chiến dịch quảng cáo
  out.push(...campaignInsights(cur, prev));

  // 6. Điểm sáng
  const topSale = [...saleRows].sort((a, b) => n(b.revenue) - n(a.revenue))[0];
  if (topSale && n(topSale.revenue) > 0) {
    out.push({ id: 'top-sale', level: 'good', dept: 'sale', title: `${topSale.name} dẫn đầu doanh thu`, detail: `${fmtMoney(n(topSale.revenue))} · chốt ${fmtN(n(topSale.closed))}/${fmtN(n(topSale.showup))} khách.` });
  }
  const topTele = [...teleRows].sort((a, b) => n(b.closed) - n(a.closed) || n(b.booked) - n(a.booked))[0];
  if (topTele && n(topTele.booked) > 0) {
    out.push({ id: 'top-tele', level: 'good', dept: 'tele', title: `${topTele.name} dẫn đầu Telesale`, detail: `${fmtN(n(topTele.booked))} lịch đặt · ${fmtN(n(topTele.showup))} khách đến · ${fmtN(n(topTele.closed))} chốt.` });
  }
  const growth = drops.find(d => d.id === 'revenue');
  if (growth && growth.p > 0 && growth.c >= growth.p * 1.2) {
    out.push({ id: 'growth', level: 'good', dept: 'sale', title: `Doanh thu tăng ${Math.round(((growth.c - growth.p) / growth.p) * 100)}%`, detail: `${fmtMoney(growth.c)} ${prevLabel} (${fmtMoney(growth.p)}).` });
  }

  const order: Record<AlertLevel, number> = { critical: 0, warning: 1, good: 2 };
  return out.sort((a, b) => order[a.level] - order[b.level]);
}

/** Tiền rút gọn: 1,25 tỷ · 125,4 tr · 850 N */
export function fmtMoney(v: number | null | undefined) {
  // API có thể trả null (ví dụ doanh thu chưa ghi nhận) — không được làm sập trang
  if (v == null || !Number.isFinite(Number(v))) return '—';
  v = Number(v);
  const a = Math.abs(v);
  if (a >= 1e9) return `${(v / 1e9).toLocaleString('vi-VN', { maximumFractionDigits: 2 })} tỷ`;
  if (a >= 1e6) return `${(v / 1e6).toLocaleString('vi-VN', { maximumFractionDigits: 1 })} tr`;
  if (a >= 1e3) return `${(v / 1e3).toLocaleString('vi-VN', { maximumFractionDigits: 0 })} N`;
  return v.toLocaleString('vi-VN');
}
export const fmtFull = (v: number | null | undefined) => (v == null || !Number.isFinite(Number(v)) ? '—' : `${Math.round(Number(v)).toLocaleString('vi-VN')} đ`);
export const fmtRate = (v: number | null | undefined) => (v == null || !Number.isFinite(v) ? '—' : `${v.toFixed(1).replace('.', ',')}%`);

// ===================== Chi phí marketing → khách hàng → doanh thu =====================

export interface CampaignRow {
  key: string;
  name: string;
  channel: string;
  active: boolean;
  budget: number;
  spent: number;
  messages: number;
  leads: number;
  revenue: number;
  /** Chi phí / tin nhắn */
  cpm: number | null;
  /** Chi phí / lead (người nhắn tin từ quảng cáo) */
  cpl: number | null;
  /** Doanh thu / chi phí */
  roas: number | null;
  /** Tin nhắn → khách hàng (%) */
  msgToLead: number | null;
}

/**
 * Gom dòng chiến dịch theo fb_campaign_id (BE có thể trả trùng id / id null).
 * Dòng trùng hoàn toàn chỉ tính một lần để không cộng đôi chi phí.
 */
export function campaignRows(mkt?: MarketingReportV2Data | null): CampaignRow[] {
  const groups = new Map<string, { name: string; channel: string; active: boolean; budget: number; spent: number; messages: number; leads: number; revenue: number; seen: Set<string> }>();
  (mkt?.campaigns || []).forEach((c, i) => {
    const key = c.fb_campaign_id || `${c.name}-${i}`;
    const sig = [c.spent, c.messages, c.leads, c.revenue, c.budget].join('|');
    let g = groups.get(key);
    if (!g) {
      g = { name: c.name || 'Chiến dịch không tên', channel: c.channel || 'Khác', active: false, budget: 0, spent: 0, messages: 0, leads: 0, revenue: 0, seen: new Set() };
      groups.set(key, g);
    }
    if ((c.effective_status || c.status) === 'ACTIVE') g.active = true;
    if (g.seen.has(sig)) return;
    g.seen.add(sig);
    g.budget += n(c.budget); g.spent += n(c.spent); g.messages += n(c.messages); g.leads += n(c.leads); g.revenue += n(c.revenue);
  });
  return Array.from(groups.entries()).map(([key, g]) => ({
    key, name: g.name, channel: g.channel, active: g.active, budget: g.budget, spent: g.spent, messages: g.messages, leads: g.leads, revenue: g.revenue,
    // chiến dịch không phát sinh chi phí thì không có đơn giá (tránh xếp hạng "0 đ/lead")
    cpm: g.spent > 0 && g.messages ? g.spent / g.messages : null,
    cpl: g.spent > 0 && g.leads ? g.spent / g.leads : null,
    roas: g.spent > 0 ? g.revenue / g.spent : null,
    msgToLead: ratio(g.leads, g.messages),
  }));
}

export interface CostStep { key: string; label: string; value: number; money?: boolean; unitCost: number | null; prevUnitCost: number | null; per1m: number | null }

const per = (spend: number, v: number) => (spend > 0 && v > 0 ? spend / v : null);

/** Chuỗi chi phí: mỗi bước của hành trình tốn bao nhiêu tiền quảng cáo, và 1 triệu chi phí mang về bao nhiêu */
export function costChain(c: Totals, p: Totals): CostStep[] {
  const items: [string, string, keyof Totals][] = [
    ['adMessages', 'Tin nhắn từ quảng cáo', 'adMessages'],
    ['leads', 'Khách hàng mới', 'leads'],
    ['booked', 'Lịch hẹn đã đặt', 'booked'],
    ['showup', 'Khách đến cơ sở', 'showup'],
    ['closed', 'Khách chốt dịch vụ', 'closed'],
  ];
  return [
    { key: 'spend', label: 'Chi phí quảng cáo', value: c.spend, money: true, unitCost: null, prevUnitCost: null, per1m: null },
    ...items.map(([key, label, k]) => {
      const v = c[k] as number;
      return { key, label, value: v, unitCost: per(c.spend, v), prevUnitCost: per(p.spend, p[k] as number), per1m: c.spend > 0 ? (v / c.spend) * 1e6 : null };
    }),
    { key: 'revenue', label: 'Doanh thu', value: c.revenue, money: true, unitCost: null, prevUnitCost: null, per1m: c.spend > 0 ? (c.revenue / c.spend) * 1e6 : null },
  ];
}

/** Ngưỡng cho chiến dịch */
export const CAMPAIGN_RULES = {
  /** Đã chi từ mức này mà chưa có khách nào → "đốt tiền" */
  wasteSpend: 1_000_000,
  /** Chi phí / khách cao hơn trung bình theo hệ số này → đắt */
  expensiveFactor: 1.5,
};

export function campaignInsights(cur: Snapshot, prev: Snapshot): Insight[] {
  const out: Insight[] = [];
  const rows = campaignRows(cur.mkt);
  if (!rows.length) return out;
  const c = totals(cur);
  const p = totals(prev);
  const avgCpl = per(c.spend, c.adLeads);

  const waste = rows.filter(r => r.spent >= CAMPAIGN_RULES.wasteSpend && r.leads === 0).sort((a, b) => b.spent - a.spent);
  if (waste.length) {
    const sum = waste.reduce((s, r) => s + r.spent, 0);
    out.push({
      id: 'camp-waste', level: 'critical', dept: 'mkt',
      title: `${waste.length} chiến dịch đã chi ${fmtMoney(sum)} chưa có lead`,
      detail: `${waste.slice(0, 2).map(r => `${r.name} (${fmtMoney(r.spent)})`).join(', ')}${waste.length > 2 ? '…' : ''}`,
    });
  }
  if (avgCpl) {
    rows.filter(r => r.cpl !== null && r.leads >= RULES.minVolume && r.cpl > avgCpl * CAMPAIGN_RULES.expensiveFactor)
      .sort((a, b) => b.spent - a.spent).slice(0, 2)
      .forEach(r => out.push({
        id: `camp-exp-${r.key}`, level: 'warning', dept: 'mkt',
        title: `${r.name}: ${fmtMoney(r.cpl!)}/lead`,
        detail: `Đắt gấp ${(r.cpl! / avgCpl).toFixed(1).replace('.', ',')} lần trung bình (${fmtMoney(avgCpl)}/lead) — đã chi ${fmtMoney(r.spent)}.`,
      }));
  }
  const cpl = per(c.spend, c.leads);
  const prevCpl = per(p.spend, p.leads);
  if (cpl && prevCpl && cpl >= prevCpl * 1.3) {
    out.push({ id: 'cpl-up', level: 'warning', dept: 'mkt', title: `Chi phí / khách mới tăng ${Math.round((cpl / prevCpl - 1) * 100)}%`, detail: `${fmtMoney(cpl)} so với ${fmtMoney(prevCpl)} kỳ trước.` });
  }
  const best = rows.filter(r => r.cpl !== null && r.leads >= RULES.minVolume).sort((a, b) => a.cpl! - b.cpl!)[0];
  if (best && avgCpl && best.cpl! < avgCpl) {
    out.push({ id: 'camp-best', level: 'good', dept: 'mkt', title: `${best.name} hiệu quả nhất`, detail: `${fmtMoney(best.cpl!)}/lead · ${fmtN(best.leads)} lead từ ${fmtMoney(best.spent)}.` });
  }
  return out;
}

export interface NetworkRow {
  name: string;
  spent: number;
  /** % trên tổng chi phí */
  share: number;
  campaigns: number;
  activeCampaigns: number;
  messages: number;
  leads: number;
  revenue: number;
  cpm: number | null;
  cpl: number | null;
  roas: number | null;
  integrated: boolean;
  note?: string;
}

const NETWORK_ALIAS: Record<string, string> = { facebook: 'Facebook', fb: 'Facebook', meta: 'Facebook', tiktok: 'TikTok', zalo: 'Zalo', google: 'Google', youtube: 'YouTube' };
const networkName = (v: string) => NETWORK_ALIAS[v.trim().toLowerCase()] || v.trim() || 'Khác';

/**
 * Chi phí quảng cáo theo từng mạng (Facebook, TikTok, …): gộp chiến dịch theo kênh, bổ sung mạng có trong
 * phần phân bổ kênh của báo cáo nhưng chưa có chiến dịch, và phần chi phí chưa phân loại (nếu tổng lệch).
 */
export function networkRows(mkt?: MarketingReportV2Data | null): NetworkRow[] {
  if (!mkt) return [];
  const map = new Map<string, NetworkRow>();
  const get = (name: string) => {
    const key = networkName(name);
    let r = map.get(key);
    if (!r) {
      r = { name: key, spent: 0, share: 0, campaigns: 0, activeCampaigns: 0, messages: 0, leads: 0, revenue: 0, cpm: null, cpl: null, roas: null, integrated: true };
      map.set(key, r);
    }
    return r;
  };
  campaignRows(mkt).forEach(c => {
    const r = get(c.channel);
    r.spent += c.spent; r.messages += c.messages; r.leads += c.leads; r.revenue += c.revenue;
    r.campaigns += 1; if (c.active) r.activeCampaigns += 1;
  });
  const sp = mkt.channel_breakdown?.spend_and_performance;
  ([['Facebook', sp?.facebook], ['TikTok', sp?.tiktok]] as const).forEach(([fallback, s]) => {
    if (!s) return;
    const r = get(s.name || fallback);
    r.integrated = s.is_integrated !== false;
    r.note = s.note;
    if (!r.spent && n(s.spent)) r.spent = n(s.spent);
    if (!r.campaigns && n(s.campaign_count)) r.campaigns = n(s.campaign_count);
  });
  const rows = Array.from(map.values());
  const total = n(mkt.summary.total_spent);
  const sum = rows.reduce((t, r) => t + r.spent, 0);
  if (total - sum > Math.max(1000, total * 0.005)) {
    rows.push({ name: 'Chưa phân loại', spent: total - sum, share: 0, campaigns: 0, activeCampaigns: 0, messages: 0, leads: 0, revenue: 0, cpm: null, cpl: null, roas: null, integrated: true, note: 'Chênh lệch giữa tổng đã chi và tổng các chiến dịch' });
  }
  const grand = Math.max(total, rows.reduce((t, r) => t + r.spent, 0));
  return rows
    .map(r => ({ ...r, share: grand ? (r.spent / grand) * 100 : 0, cpm: per(r.spent, r.messages), cpl: per(r.spent, r.leads), roas: r.spent ? r.revenue / r.spent : null }))
    .sort((a, b) => b.spent - a.spent || b.messages - a.messages);
}
